import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  TrendingUp,
  BarChart3,
  Users,
  FileText,
  Calendar,
  MessageCircle,
  Target,
  ArrowRight,
  Quote,
  CheckCircle,
} from "lucide-react";

const monitoringMethods = [
  {
    icon: Calendar,
    title: "Regular Keywork Sessions",
    description:
      "Structured one-to-one sessions with keyworkers to review progress, set goals and address challenges.",
  },
  {
    icon: FileText,
    title: "Support Planning",
    description:
      "Individualised support plans that are regularly reviewed and updated based on progress and changing needs.",
  },
  {
    icon: Users,
    title: "Progress Reviews",
    description:
      "Formal review meetings with young people and professionals to assess outcomes and adjust support.",
  },
  {
    icon: MessageCircle,
    title: "Multi-Agency Communication",
    description:
      "Regular communication with social workers, education providers and other involved professionals.",
  },
];

const outcomeAreas = [
  {
    title: "Accommodation Stability",
    description: "Young people maintaining their placement and building positive tenancy skills",
    metrics: ["Placement retention", "Move-on readiness", "Independent living skills"],
  },
  {
    title: "Education & Employment",
    description: "Engagement with education, training or employment opportunities",
    metrics: ["EET engagement", "Qualifications gained", "Work experience"],
  },
  {
    title: "Health & Wellbeing",
    description: "Improvements in physical and emotional health and wellbeing",
    metrics: ["Mental health engagement", "GP registration", "Healthy lifestyle choices"],
  },
  {
    title: "Relationships & Safety",
    description: "Developing positive relationships and reducing risky behaviours",
    metrics: ["Reduced missing episodes", "Positive peer connections", "Family contact"],
  },
];

const caseStudies = [
  {
    initials: "JT",
    age: 18,
    title: "Building Independence",
    summary:
      "After placement breakdown and previous missing episodes, this young person has now maintained stable accommodation for 8 months, re-engaged with college, and developed strong life skills ready for independent living.",
    outcomes: ["Stable placement", "College enrolment", "Part-time employment"],
  },
  {
    initials: "SK",
    age: 17,
    title: "Finding a Voice",
    summary:
      "Working through our Visionary Programme, this young woman has developed confidence, improved her mental health and is now actively participating in decisions about her future and supporting other young people.",
    outcomes: ["Improved wellbeing", "Active participation", "Peer mentoring"],
  },
];

export default function ImpactPage() {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
        <div className="blob-teal -top-40 -right-40" />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <TrendingUp className="w-4 h-4" />
                Impact & Outcomes
              </div>
              <h1
                className="text-4xl sm:text-5xl font-extrabold text-slate-900 leading-tight mb-6"
                data-testid="impact-headline"
              >
                Measuring what{" "}
                <span className="text-teal-600">matters most</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed">
                We're committed to demonstrating the positive impact of our work
                and continuously improving outcomes for young people.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How We Monitor */}
      <section className="section-padding bg-white" data-testid="monitoring-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              How We Monitor Progress
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Our approach to tracking and supporting positive outcomes
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {monitoringMethods.map((method, index) => (
              <motion.div
                key={method.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <method.icon className="w-8 h-8 text-teal-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">
                  {method.title}
                </h3>
                <p className="text-slate-600 text-sm">{method.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Outcome Areas */}
      <section className="section-padding bg-slate-50" data-testid="outcomes-areas-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Outcome Areas
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Key areas where we aim to support positive change
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {outcomeAreas.map((area, index) => (
              <motion.div
                key={area.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-900 text-lg">
                    {area.title}
                  </h3>
                </div>
                <p className="text-slate-600 mb-4">{area.description}</p>
                <div className="flex flex-wrap gap-2">
                  {area.metrics.map((metric) => (
                    <span
                      key={metric}
                      className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-sm"
                    >
                      {metric}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="section-padding bg-white" data-testid="case-studies-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Success Stories
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Anonymous examples of young people we've supported (details changed
              for confidentiality)
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {caseStudies.map((study, index) => (
              <motion.div
                key={study.initials}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-slate-50 rounded-3xl p-8"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                    {study.initials}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">
                      {study.title}
                    </h3>
                    <p className="text-slate-500 text-sm">Age {study.age}</p>
                  </div>
                </div>
                <Quote className="w-8 h-8 text-slate-200 mb-4" />
                <p className="text-slate-600 leading-relaxed mb-6">
                  {study.summary}
                </p>
                <div className="space-y-2">
                  {study.outcomes.map((outcome) => (
                    <div
                      key={outcome}
                      className="flex items-center gap-2 text-sm"
                    >
                      <CheckCircle className="w-4 h-4 text-teal-500" />
                      <span className="text-slate-700">{outcome}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-slate-500 text-sm mt-8">
            * All case studies are anonymised and details have been changed to
            protect confidentiality
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-teal-700" data-testid="impact-cta-section">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Want to know more about our impact?
          </h2>
          <p className="text-teal-100 text-lg mb-8 max-w-xl mx-auto">
            Get in touch to discuss our outcomes and approach in more detail.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-white text-teal-700 hover:bg-slate-100 rounded-full px-8"
                data-testid="impact-contact-btn"
              >
                Contact Us
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/referrals">
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/10 rounded-full px-8"
                data-testid="impact-referral-btn"
              >
                Make a Referral
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
